// lib/api.ts
import { API_BASE_URL, apiUrl } from '@/config';
import { ensureFreshToken } from '@/lib/tokenRefresh';

// Mantengo BASE_URL por compat, pero ya no lo usamos directamente en fetch.
const BASE_URL = API_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY || localStorage.getItem('mcp_api_key') || ''; 

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
  'Accept': 'application/json',
};

// Manejo estándar de respuestas
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    if (response.status === 401) {
      console.warn('🚨 Respuesta 401 - Sesión expirada o inválida');
      // Limpiar tokens cuando hay error de autenticación
      localStorage.removeItem('auth_token');
      localStorage.removeItem('org_id');
      // Disparar evento global para que la App cierre sesión
      window.dispatchEvent(new Event('auth:logout'));
      throw new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
    }

    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const errorData = await response.json();
      console.error('❌ Error de API:', errorData);
      // `status` adjunto (aditivo): permite distinguir 403/404 sin parsear el texto.
      throw Object.assign(
        new Error(errorData.detail || `Request failed with status ${response.status}`),
        { status: response.status },
      );
    }

    console.error('❌ Error de API sin JSON:', response.status, response.statusText);
    throw Object.assign(new Error(`Request failed with status ${response.status}`), { status: response.status });
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }

  return response.text();
};

// Headers con token (para endpoints protegidos)
const headersWithToken = async () => {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) {
    console.error('❌ No se encontró token de autenticación en localStorage');
    throw new Error('No authentication token found');
  }
  return {
    ...defaultHeaders,
    Authorization: `Bearer ${token}`,
  };
};

// Headers con token opcional (si no hay token, no rompe)
const headersWithTokenOptional = () => {
  const token = localStorage.getItem('auth_token');
  return token
    ? { ...defaultHeaders, Authorization: `Bearer ${token}` }
    : { ...defaultHeaders };
};

// Login
export async function login(username: string, password: string) {
  try {
    const res = await fetch(apiUrl('/auth/login'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ username, password }),
    });
    const data = await handleResponse(res);
    return data; // { access_token, token_type, org_id }
  } catch (error) {
    throw error;
  }
}

// Register
export async function register(username: string, password: string, email: string, telefono: string, org_id: string) {
  try {
    const res = await fetch(apiUrl('/auth/register'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ username, password, email, telefono, org_id }),
    });
    const data = await handleResponse(res);
    return data; // { access_token, token_type, org_id }
  } catch (error) {
    throw error;
  }
}

// Nueva Autenticación: Inicio de registro (Role Upgrade / Nuevo Usuario)
export async function registerStart(username: string, email: string, telefono: string, password?: string, requested_role: string = 'admin') {
  try {
    const res = await fetch(apiUrl('/auth/register/start'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ username, email, telefono, password, requested_role }),
    });
    const data = await handleResponse(res);
    return data; // { success, message, registration_id }
  } catch (error) {
    throw error;
  }
}

// Nueva Autenticación: Verificar email
export async function registerVerifyEmail(registration_id: string, code: string) {
  try {
    const res = await fetch(apiUrl('/auth/register/verify-email'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ registration_id, code }),
    });
    const data = await handleResponse(res);
    return data; // { success, message, status }
  } catch (error) {
    throw error;
  }
}

// Forgot Password - Solicita recuperación vía Orión (WhatsApp)
export async function forgotPassword(telefono: string): Promise<{
  success: boolean;
  message: string;
  request_id?: string;
}> {
  try {
    const res = await fetch(apiUrl('/auth/forgot-password'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ telefono }),
    });
    const data = await handleResponse(res);
    return data;
  } catch (error) {
    throw error;
  }
}

// Obtener conversaciones
export async function getConversations(
  telefono?: string,
  sessionId?: string,
  date?: string
) {
  try {
    const params = new URLSearchParams();
    if (telefono) params.append('telefono', telefono);
    if (sessionId) params.append('session_id', sessionId);
    if (date) params.append('query_date', date);

    const url = apiUrl('/conversaciones') + (params.toString() ? `?${params.toString()}` : '');

    const response = await fetch(url, {
      headers: await headersWithToken(),
    });

    const data = await handleResponse(response);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Get conversations error:', error);
    throw error;
  }
}

// Insight consolidado de las últimas 2 semanas
export async function getSummary14d() {
  try {
    const res = await fetch(apiUrl('/api/insights/summary-14d'), {
      headers: await headersWithToken(),
    });

    return handleResponse(res);
  } catch (error) {
    console.error('Error al obtener resumen de 14 días:', error);
    throw error;
  }
}

export async function getLeads(skip = 0, limit = 100) {
  const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  const res = await fetch(apiUrl(`/leads/`) + `?${params.toString()}`, {
    headers: await headersWithToken(),
  });
  const data = await handleResponse(res);

  if (Array.isArray(data)) return data;
  if (Array.isArray((data as any)?.items)) return (data as any).items;
  if (Array.isArray((data as any)?.results)) return (data as any).results;
  return [];
}


// ─── Sprint R4 Remarketing automatizado (multi-tenant strict via JWT) ─

export type RemarketingState = 'off' | 'auto' | 'paused';
export type RemarketingChannel = 'email' | 'whatsapp' | 'both' | 'none';

export type RemarketingInfo = {
  lead_id: string;
  customer_id: string;
  remarketing_enabled: boolean;
  remarketing_state: RemarketingState;
  remarketing_channel: RemarketingChannel;
  remarketing_paused_until: string | null;
  marketing_stage: string;
  last_remarketing_sent_at: string | null;
  last_remarketing_channel: string | null;
  unsubscribed: boolean;
};

export async function toggleLeadRemarketing(leadId: string, enabled: boolean): Promise<RemarketingInfo> {
  const res = await fetch(apiUrl(`/leads/${leadId}/remarketing`), {
    method: 'PATCH',
    headers: { ...(await headersWithToken()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  return handleResponse(res);
}

export async function bulkToggleLeadRemarketing(leadIds: string[], enabled: boolean): Promise<{ updated: number; skipped: number; requested: number }> {
  const res = await fetch(apiUrl(`/leads/bulk-remarketing`), {
    method: 'POST',
    headers: { ...(await headersWithToken()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ lead_ids: leadIds, enabled }),
  });
  return handleResponse(res);
}

export async function promoteLead(leadId: string, stage: string = 'prospecto'): Promise<RemarketingInfo> {
  const res = await fetch(apiUrl(`/leads/${leadId}/promote`), {
    method: 'POST',
    headers: { ...(await headersWithToken()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ stage }),
  });
  return handleResponse(res);
}

export async function unsubscribeLead(leadId: string): Promise<RemarketingInfo> {
  const res = await fetch(apiUrl(`/leads/${leadId}/unsubscribe`), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function pauseLeadRemarketing(
  leadId: string,
  opts: { until?: string | null; days?: number } = {},
): Promise<RemarketingInfo> {
  const res = await fetch(apiUrl(`/leads/${leadId}/pause-remarketing`), {
    method: 'POST',
    headers: { ...(await headersWithToken()), 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  return handleResponse(res);
}

// Sales · Presentations / Lead Landings: ver `@/modules/leads-import`
// (usa el proxy /django-api, no el de Orion FastAPI).

// Obtener threads agregados
// Opcional: describe lo que sale de /threads (ThreadOut)
export type ThreadDTO = {
  id: string;
  session_id: string;
  telefono: string;
  status: string;
  priority: string;                  // 'urgent' | 'atender' | 'neutral'
  last_message_at: string;           // ISO
  last_message_preview?: string|null;
  last_sentiment?: string|null;      // 'positivo' | 'neutro' | 'negativo'
  next_appointment_start?: string|null; // <- viene del backend
  owner_user_id?: string|null;
  unread_admin_count?: number;       // puede faltar
  org_id?: string;
  customer_id?: string|null;
  meta?: any;
  // 🙋 Control humano (takeover): estado del agente en esta conversación.
  agent_paused?: boolean;
  agent_paused_until?: string|null;      // ISO; null si indefinido o activo
  agent_paused_indefinite?: boolean;
  agent_paused_by?: string|null;
  // De dónde viene la conversación y quién es (Orión db/conversation_channel.py).
  channel?: string|null;   // panel | whatsapp | facebook | instagram | web | voice_realtime | voice_phone | email | api | unknown
  party?: string|null;     // external | internal | test | unknown
  // 📱 App de WhatsApp (Orión services/whatsapp_reply_mode.py). Aditivos.
  pending_suggestion?: { text: string; at?: string | null; source?: 'pause' | 'mode' | string | null } | null;
  reply_mode?: 'auto' | 'suggest' | 'off' | null;   // anulación en ESTA conversación (null = hereda)
  wa_number?: string | null;                        // meta:<id> | twilio:<dígitos>
};

export async function getThreads(params?: {
  status?: string; priority?: string; owner_user_id?: string; search?: string;
  limit?: number; offset?: number; channel?: string;
}): Promise<ThreadDTO[]> {
  const q = new URLSearchParams({
    limit: String(params?.limit ?? 200),
    offset: String(params?.offset ?? 0),
  });
  if (params?.status) q.set('status', params.status);
  if (params?.priority) q.set('priority', params.priority);
  if (params?.owner_user_id) q.set('owner_user_id', params.owner_user_id);
  if (params?.search) q.set('search', params.search);
  if (params?.channel) q.set('channel', params.channel);

  const res = await fetch(apiUrl('/threads') + `/?${q}`, { headers: await headersWithToken() });
  return handleResponse(res);
}

// ── Bandeja v1 (Orión GET /threads/inbox): Clientes / Equipo / Pruebas ──
// Cada fila trae canal, parte, persona (lead/cliente del CRM o usuario del
// equipo) y la capa de misión del agente. La privacidad la decide el SERVIDOR:
// lo interno solo lo ve su dueño o admin/cto. Si el backend aún no tiene esta
// ruta, `getInbox` devuelve `null` y la bandeja usa /threads/ como antes.
export type InboxParty = 'external' | 'internal' | 'test';
export type InboxPerson = {
  kind: 'lead' | 'customer' | 'team' | 'unknown';
  name: string | null;
  customer_id: string | null;
  stage: string | null;
  user_id: string | null;
  linked?: boolean;
};
export type InboxItem = ThreadDTO & {
  title?: string | null;
  person?: InboxPerson | null;
  agent_layer?: 'interno' | 'comercial' | string;
};
export type InboxResponse = {
  ok: boolean;
  api: string;
  tab: InboxParty;
  viewer: { can_see_team: boolean };
  org_has_interno_layer: boolean;
  counts: {
    party: Record<InboxParty, number>;
    channel: Record<string, number>;
    priority: Record<string, number>;
  };
  items: InboxItem[];
};

export async function getInbox(
  params: { party: InboxParty; channel?: string; search?: string; limit?: number; offset?: number;
    /** canal que vive en su propia app (p. ej. 'whatsapp') → fuera de esta bandeja */
    excludeChannel?: string },
  opts?: { signal?: AbortSignal },
): Promise<InboxResponse | null> {
  const q = new URLSearchParams({
    party: params.party,
    limit: String(params.limit ?? 200),
    offset: String(params.offset ?? 0),
  });
  if (params.channel) q.set('channel', params.channel);
  if (params.search) q.set('search', params.search);
  if (params.excludeChannel) q.set('exclude_channel', params.excludeChannel);
  const res = await fetch(apiUrl('/threads/inbox') + `?${q}`, {
    headers: await headersWithToken(),
    signal: opts?.signal,
  });
  // Backend sin la ruta nueva: /threads/{thread_id} la confunde con un id (404/405/422).
  if (res.status === 404 || res.status === 405 || res.status === 422) return null;
  const data = await handleResponse(res);
  return data && typeof data === 'object' && Array.isArray((data as any).items) ? (data as InboxResponse) : null;
}

/** Hilos de un visitante/persona para la ficha de trazabilidad (Orión GET /visitor-threads).
 *  Por `meta.visitor_id` (chat del sitio) y/o teléfono del lead. Org del JWT; nunca internos.
 *  `null` si este Orión todavía no tiene la ruta. */
export type VisitorThread = {
  id: string; session_id: string; telefono: string; status: string; channel: string; party: string;
  last_message_at: string; last_message_preview?: string | null; title?: string | null;
  matched_by: 'visitor_id' | 'phone'; first_touch?: Record<string, string> | null;
};
export async function getVisitorThreads(
  params: { visitor_id?: string; phones?: string[] },
): Promise<{ threads: VisitorThread[] } | null> {
  const q = new URLSearchParams();
  if (params.visitor_id) q.set('visitor_id', params.visitor_id);
  if (params.phones?.length) q.set('phone', params.phones.slice(0, 5).join(','));
  if (!q.toString()) return { threads: [] };
  const res = await fetch(apiUrl('/visitor-threads') + `?${q}`, { headers: await headersWithToken() });
  if (res.status === 404 || res.status === 405) return null;
  const data = await handleResponse(res);
  return data && Array.isArray((data as any).threads) ? (data as { threads: VisitorThread[] }) : { threads: [] };
}

export async function getMyThreads(): Promise<{
  ok: boolean;
  threads: any[];
  main_phone: string;
  message?: string;
}> {
  const res = await fetch(apiUrl('/threads/me/list'), { headers: await headersWithToken() });
  return handleResponse(res);
}


// ── Chat: modelo por conversación y títulos ──────────────────
export interface ChatEffortOption {
  id: string;
  label: string;
  description: string;
}

export interface ChatModelOption {
  id: string;
  label: string;
  provider: string;
  /** Grupo del selector (OpenAI, Anthropic…). Backend viejo: no viene. */
  group?: string;
  description: string;
  efforts: ChatEffortOption[];
  default_effort: string | null;
}

/** Proveedor anunciado pero NO elegible (logo + «Próximamente»). */
export interface ChatModelComingSoon {
  id: string;
  label: string;
  provider: string;
  group: string;
  description?: string;
  coming_soon: true;
}

export interface ChatModelsInfo {
  enabled: boolean;
  sovereign: boolean;
  auto: { id: 'auto'; label: string; description: string; resolves_to?: string | null; resolves_to_label?: string | null };
  models: ChatModelOption[];
  /** Orden de grupos por proveedor (aditivo). */
  groups?: Array<{ provider: string; group: string }>;
  coming_soon?: ChatModelComingSoon[];
}

export interface ChatModelChoice {
  model: string; // 'auto' | id de modelo
  effort: string | null;
}

/** Modelos que el usuario puede elegir en el chat. `null` si el backend todavía
 *  no tiene el endpoint (404/405) o falla: el selector simplemente no aparece. */
export async function getChatModels(): Promise<ChatModelsInfo | null> {
  try {
    const res = await fetch(apiUrl('/threads/models'), { headers: await headersWithToken() });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    if (!data || !Array.isArray(data.models)) return null;
    return data as ChatModelsInfo;
  } catch {
    return null;
  }
}

/** Renombrar una conversación propia. Devuelve el ThreadOut actualizado. */
export async function renameThread(threadId: string, title: string) {
  const res = await fetch(apiUrl(`/threads/${threadId}`), {
    method: 'PATCH',
    headers: await headersWithToken(),
    body: JSON.stringify({ title }),
  });
  return handleResponse(res);
}

/** Título visible de un hilo del historial: el nombre (manual o automático) o,
 *  para hilos viejos sin nombre, el inicio del último mensaje. */
export function threadDisplayTitle(t: { title?: string | null; last_message_preview?: string | null }): string {
  const s = (t.title || t.last_message_preview || '').toString().trim();
  return (s || 'Nueva conversación').slice(0, 70);
}

/** GET autenticado genérico contra Orión (`/orion/api`). Mismo manejo de
 *  sesión/errores que el resto de este archivo. Lo usa el shell «Orion». */
export async function orionGet<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path), { headers: await headersWithToken() });
  return handleResponse(res);
}

// ═══════════════════════════════════════════════════════════════
// Contactos (Directorio)
// ═══════════════════════════════════════════════════════════════

export type ContactDTO = {
  id: string;
  org_id: string;
  nombre: string;
  apellido: string | null;
  email: string | null;
  telefono: string | null;
  puesto: string | null;
  departamento: string | null;
  foto: string | null;
  canal_preferido: string | null;
  source: 'system' | 'org' | 'personal';
  owner_user_id: string | null;
  linked_user_id: string | null;
  notas: string | null;
  tags: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export async function getContacts(search?: string, source?: string): Promise<ContactDTO[]> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (source) params.set('source', source);
  const qs = params.toString();
  const res = await fetch(apiUrl('/contacts/') + (qs ? `?${qs}` : ''), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function createContact(data: Partial<ContactDTO>): Promise<ContactDTO> {
  const res = await fetch(apiUrl('/contacts/'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateContact(id: string, data: Partial<ContactDTO>): Promise<ContactDTO> {
  const res = await fetch(apiUrl(`/contacts/${id}`), {
    method: 'PATCH',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteContact(id: string): Promise<void> {
  const res = await fetch(apiUrl(`/contacts/${id}`), {
    method: 'DELETE',
    headers: await headersWithToken(),
  });
  if (!res.ok) throw new Error('Error eliminando contacto');
}

export async function syncContacts(): Promise<{ synced: number; message: string }> {
  const res = await fetch(apiUrl('/contacts/sync'), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}// Asignar dueño del hilo
export async function assignThreadOwner(thread_id: string, owner_user_id: string) {
  const res = await fetch(apiUrl(`/threads/${thread_id}/assign`), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify({ owner_user_id }),
  });
  return handleResponse(res); // ThreadOut
}

// Actualizar estado/prioridad del hilo
export async function updateThread(
  thread_id: string,
  body: { status?: string; priority?: string }
) {
  const res = await fetch(apiUrl(`/threads/${thread_id}`), {
    method: 'PATCH',
    headers: await headersWithToken(),
    body: JSON.stringify(body),
  });
  return handleResponse(res); // ThreadOut
}

// 🙋 Control humano (takeover): prender/apagar el agente en una conversación.
// Se identifica por thread_id | session_id | telefono (lo que la superficie
// tenga a mano). El org_id lo pone el backend desde el token (multi-tenant).
export type AgentConvKey = {
  thread_id?: string | null;
  session_id?: string | null;
  telefono?: string | null;
};

// 🔔 Notificaciones de la org (Orión /org/notificaciones): correos que reciben los avisos
// (conversación nueva, prospecto nuevo, alertas del supervisor). Org del JWT; guardar = admin.
export type OrgNotificaciones = {
  emails: string[]; on_new_conversation: boolean; on_new_lead: boolean; tiene_whatsapp_admin?: boolean;
};

export async function getOrgNotificaciones(): Promise<OrgNotificaciones> {
  const res = await fetch(apiUrl('/org/notificaciones'), { headers: await headersWithToken() });
  return handleResponse(res);
}

export async function saveOrgNotificaciones(cfg: Omit<OrgNotificaciones, 'tiene_whatsapp_admin'>): Promise<OrgNotificaciones> {
  const res = await fetch(apiUrl('/org/notificaciones'), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify(cfg),
  });
  return handleResponse(res);
}

// 🛎️ Supervisor (Orión GET /supervisor/alertas): clientes que esperan a una persona
// del equipo y nadie los ha atendido. Org del JWT; a una vendedora solo lo suyo.
// `null` si este Orión todavía no tiene la ruta.
export type SupervisorAlert = {
  thread_id: string; session_id: string; telefono: string; channel?: string | null;
  owner_user_id?: string | null; estado: 'abierta' | 'atendida'; cliente?: string | null;
  desde: string; vence: string; urgente: boolean; vencida?: boolean;
  motivos: string[]; grupo?: number; fecha_servicio?: string | null; nivel_avisado?: number;
  atendida_en?: string; como?: string;
};
export type SupervisorAlerts = { abiertas: SupervisorAlert[]; atendidas: SupervisorAlert[]; vencidas: number };

export async function getSupervisorAlerts(): Promise<SupervisorAlerts | null> {
  const res = await fetch(apiUrl('/supervisor/alertas'), { headers: await headersWithToken() });
  if (res.status === 404 || res.status === 405) return null;
  const data = await handleResponse(res);
  return data && Array.isArray((data as any).abiertas) ? (data as SupervisorAlerts) : null;
}

// «Lo atiendo»: cierra la alerta (p. ej. se atendió por teléfono).
export async function attendSupervisorAlert(threadId: string): Promise<unknown> {
  const res = await fetch(apiUrl(`/supervisor/alertas/${encodeURIComponent(threadId)}/atender`), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Pausar el agente: `minutes` null/omitido = indefinido. Devuelve ThreadOut.
export async function pauseThreadAgent(
  key: AgentConvKey,
  minutes?: number | null,
): Promise<ThreadDTO> {
  const res = await fetch(apiUrl('/threads/agent-control'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify({ action: 'pause', minutes: minutes ?? null, ...key }),
  });
  return handleResponse(res);
}

// Reanudar: devuelve el control al agente. Devuelve ThreadOut.
export async function resumeThreadAgent(key: AgentConvKey): Promise<ThreadDTO> {
  const res = await fetch(apiUrl('/threads/agent-control'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify({ action: 'resume', ...key }),
  });
  return handleResponse(res);
}

// Estado del control (para superficies sin el objeto thread en mano).
// 🙋 Takeover: el staff envía un mensaje POR el ciudadano (agente en pausa).
// El backend despacha al canal ACTIVO de la org (Twilio o Meta, se resuelve solo).
export async function sendHumanMessage(threadId: string, text: string): Promise<{ ok: boolean }> {
  const res = await fetch(apiUrl(`/threads/${threadId}/human-message`), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify({ text }),
  });
  return handleResponse(res);
}

export async function getThreadAgentState(key: AgentConvKey): Promise<ThreadDTO> {
  const q = new URLSearchParams();
  if (key.thread_id) q.set('thread_id', key.thread_id);
  if (key.session_id) q.set('session_id', key.session_id);
  if (key.telefono) q.set('telefono', key.telefono);
  const res = await fetch(apiUrl('/threads/agent-state') + `?${q.toString()}`, {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Obtener recursos asociados a un thread
export type ThreadResourceDTO = {
  id: string;
  type: string;
  format: string;
  path: string;
  meta?: any;
  created_at: string;
};

export async function getThreadResources(thread_id: string): Promise<ThreadResourceDTO[]> {
  const res = await fetch(apiUrl(`/threads/${thread_id}/resources`), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Obtener nombre resumido del contacto si existe (desde thread meta)
export async function getContactName(session_id: string): Promise<string | null> {
  // Por ahora retornamos null - el nombre se puede extraer del meta del thread
  // o de una búsqueda futura en customers/leads
  return null;
}


export async function getOrgInfo() {
  const res = await fetch(apiUrl('/org/info'), {
    headers: headersWithTokenOptional(),
  });
  return handleResponse(res);
}

export async function getLlamadas() {
  try {
    const response = await fetch(apiUrl('/llamadas'), {
      headers: await headersWithToken(),
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error al obtener llamadas:', error);
    throw error;
  }
}

// --- Customers -------------------------------------------------------------
export type CustomerBasic = {
  id: string;
  nombre?: string | null;
  telefono?: string | null;
  email?: string | null;
  avatar_url?: string | null;
};

export async function getCustomers(
  skip = 0,
  limit = 500,
  q?: string
): Promise<CustomerBasic[]> {
  const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  if (q) params.set("q", q);
  const res = await fetch(apiUrl(`/customers/`) + `?${params.toString()}`, {
    headers: await headersWithToken(),
  });
  const data = await handleResponse(res);
  return Array.isArray(data) ? data : [];
}

export async function getCustomerDetail(customer_id: string) {
  const res = await fetch(apiUrl(`/customers/${customer_id}`), {
    headers: await headersWithToken(),
  });
  return handleResponse(res); // viene serializado seguro (jsonable_encoder)
}

// --- Dev Tasks -------------------------------------------------------------
export type DevTaskData = {
  category: string;
  prio: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'waiting' | 'done';
  assigned: string;
  detail: string;
  desc: string;
  comments?: string;
};

// Map of ID -> Data
export type DevTasksCollection = Record<string, DevTaskData>;

export async function getDevTasks(): Promise<DevTasksCollection> {
  const res = await fetch(apiUrl('/dev-tasks/'), {
    headers: await headersWithToken(),
    cache: 'no-store',
  });
  return handleResponse(res);
}

export async function saveDevTasks(tasks: DevTasksCollection): Promise<DevTasksCollection> {
  const res = await fetch(apiUrl('/dev-tasks/'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(tasks),
  });
  return handleResponse(res);
}

// =====================
// PROMPTS API
// =====================

export interface PromptLayer {
  type: string;
  name: string;
  scope_id: string | null;
  content: string;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface ThreadPromptsResponse {
  thread_id: string;
  layers: PromptLayer[];
  combined: string;
}

// Obtener prompts de un thread (combinados)
export async function getThreadPrompts(threadId: string): Promise<ThreadPromptsResponse> {
  const res = await fetch(apiUrl(`/threads/${threadId}/prompt`), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Actualizar/crear prompt de thread
export async function updateThreadPrompt(threadId: string, content: string): Promise<any> {
  const res = await fetch(apiUrl(`/threads/${threadId}/prompt`), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify({ content }),
  });
  return handleResponse(res);
}

// Resetear prompt de thread (eliminar personalización)
export async function resetThreadPrompt(threadId: string): Promise<any> {
  const res = await fetch(apiUrl(`/threads/${threadId}/prompt/reset`), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// ============== WhatsApp Templates ==============

export interface WhatsAppTemplate {
  sid: string;
  friendly_name: string;
  language: string;
  type: string;
  body: string;
  date_created: string;
  date_updated: string;
  approval_requests?: {
    status?: string;
    rejection_reason?: string;
  };
}

export interface WhatsAppTemplateResponse {
  success: boolean;
  content_sid?: string;
  friendly_name?: string;
  status?: string;
  approval_status?: string;
  message?: string;
  error?: string;
}

export interface WhatsAppTemplateListResponse {
  success: boolean;
  templates: WhatsAppTemplate[];
  count: number;
  error?: string;
}

export interface CreateTemplatePayload {
  friendly_name: string;
  body: string;
  language?: string;
  variables?: Record<string, string>;
  submit_for_approval?: boolean;
  category?: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
}

export interface SendTemplatePayload {
  template_sid: string;
  to_number: string;
  variables?: Record<string, string>;
}

// Listar todas las plantillas de WhatsApp
export async function getWhatsAppTemplates(): Promise<WhatsAppTemplateListResponse> {
  const res = await fetch(apiUrl('/whatsapp-templates/'), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Crear nueva plantilla
export async function createWhatsAppTemplate(payload: CreateTemplatePayload): Promise<WhatsAppTemplateResponse> {
  const res = await fetch(apiUrl('/whatsapp-templates/'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// Obtener status de una plantilla
export async function getWhatsAppTemplateStatus(templateSid: string): Promise<WhatsAppTemplateResponse> {
  const res = await fetch(apiUrl(`/whatsapp-templates/${templateSid}`), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Eliminar una plantilla
export async function deleteWhatsAppTemplate(templateSid: string): Promise<WhatsAppTemplateResponse> {
  const res = await fetch(apiUrl(`/whatsapp-templates/${templateSid}`), {
    method: 'DELETE',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Enviar mensaje usando plantilla
export async function sendWhatsAppTemplate(payload: SendTemplatePayload): Promise<WhatsAppTemplateResponse> {
  const res = await fetch(apiUrl('/whatsapp-templates/send'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// Enviar plantilla a aprobación
export async function submitTemplateForApproval(
  templateSid: string, 
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION' = 'UTILITY'
): Promise<WhatsAppTemplateResponse> {
  const res = await fetch(apiUrl(`/whatsapp-templates/${templateSid}/submit-approval?category=${category}`), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}


// =====================
// VACANCIES (HR) API
// =====================

export type VacancyDTO = {
  id: string;
  org_id: string;
  title: string;
  department: string | null;
  description: string;
  requirements: string[];
  skills_required: string[];
  nice_to_have: string[];
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  salary_period: string;
  show_salary: boolean;
  location: string | null;
  remote_policy: string;
  employment_type: string;
  status: string;
  generated_copy: string | null;
  copy_tone: string;
  max_candidates: number | null;
  deadline: string | null;
  applications_count: number;
  created_at: string | null;
  updated_at: string | null;
};

export async function getVacancies(params?: {
  status?: string;
  department?: string;
  limit?: number;
  offset?: number;
}): Promise<{ ok: boolean; vacancies: VacancyDTO[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.department) q.set('department', params.department);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  const qs = q.toString();
  const res = await fetch(apiUrl('/vacancies/') + (qs ? `?${qs}` : ''), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function getVacancy(id: string): Promise<{ ok: boolean; vacancy: VacancyDTO }> {
  const res = await fetch(apiUrl(`/vacancies/${id}`), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function getVacancyStats(): Promise<any> {
  const res = await fetch(apiUrl('/vacancies/stats'), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function createVacancy(data: {
  title: string;
  description: string;
  department?: string;
  requirements?: string[];
  skills_required?: string[];
  nice_to_have?: string[];
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  salary_period?: string;
  show_salary?: boolean;
  location?: string;
  remote_policy?: string;
  employment_type?: string;
  copy_tone?: string;
  max_candidates?: number;
  deadline?: string;
}): Promise<{ success: boolean; vacancy: VacancyDTO }> {
  const res = await fetch(apiUrl('/vacancies/'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateVacancy(id: string, data: Record<string, any>): Promise<{ success: boolean; vacancy: VacancyDTO }> {
  const res = await fetch(apiUrl(`/vacancies/${id}`), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateVacancyStatus(id: string, status: string): Promise<{ success: boolean; vacancy: VacancyDTO; message: string }> {
  const res = await fetch(apiUrl(`/vacancies/${id}/status`), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify({ status }),
  });
  return handleResponse(res);
}

export async function deleteVacancy(id: string): Promise<{ success: boolean; message: string }> {
  const res = await fetch(apiUrl(`/vacancies/${id}`), {
    method: 'DELETE',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// =====================
// CANDIDATES & RECRUITMENT API
// =====================

export type CandidateDTO = {
  id: string;
  org_id: string;
  vacancy_id: string;
  candidate_name: string;
  candidate_phone: string | null;
  candidate_email: string | null;
  cv_resource_id: string | null;
  source: string;
  status: string;
  ai_score: number | null;
  ai_summary: string | null;
  ai_extracted_data: Record<string, any> | null;
  assigned_recruiter_id: string | null;
  stage_history: Array<Record<string, any>> | null;
  thread_id: string | null;
  evaluated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type RecruitmentStats = {
  total_applications: number;
  by_status: Record<string, number>;
  avg_ai_score: number | null;
  top_candidates: Array<Record<string, any>>;
};

export type EvaluationResultDTO = {
  application_id: string;
  candidate_name: string;
  ai_score: number;
  ai_summary: string;
  ai_extracted_data: Record<string, any>;
  vacancy_title: string;
  status: string;
  recommendation: string;
};

export async function getCandidates(params?: {
  org_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ total: number; applications: CandidateDTO[] }> {
  const q = new URLSearchParams();
  q.set('org_id', params?.org_id || localStorage.getItem('org_id') || 'orion');
  if (params?.status) q.set('status', params.status);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  const res = await fetch(apiUrl('/candidates') + `?${q.toString()}`, {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function getCandidatesByVacancy(vacancyId: string, params?: {
  status?: string;
  min_score?: number;
  limit?: number;
  offset?: number;
}): Promise<{ vacancy_id: string; total: number; applications: CandidateDTO[] }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.min_score) q.set('min_score', String(params.min_score));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  const qs = q.toString();
  const res = await fetch(apiUrl(`/candidates/vacancy/${vacancyId}`) + (qs ? `?${qs}` : ''), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function getCandidate(id: string): Promise<CandidateDTO> {
  const res = await fetch(apiUrl(`/candidates/${id}`), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function createCandidate(data: {
  vacancy_id: string;
  candidate_name: string;
  candidate_phone?: string;
  candidate_email?: string;
  cv_resource_id?: string;
  source?: string;
}): Promise<CandidateDTO> {
  const q = new URLSearchParams();
  q.set('org_id', localStorage.getItem('org_id') || 'orion');
  const res = await fetch(apiUrl('/candidates') + `?${q.toString()}`, {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function evaluateCV(data: {
  vacancy_id: string;
  candidate_name?: string;
  candidate_phone?: string;
  candidate_email?: string;
  cv_resource_id?: string;
  cv_file_path?: string;
  source?: string;
}): Promise<EvaluationResultDTO> {
  const q = new URLSearchParams();
  q.set('org_id', localStorage.getItem('org_id') || 'orion');
  const res = await fetch(apiUrl('/candidates/evaluate') + `?${q.toString()}`, {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateCandidate(id: string, data: {
  candidate_name?: string;
  candidate_phone?: string;
  candidate_email?: string;
  extra_data?: Record<string, any>;
}): Promise<CandidateDTO> {
  const res = await fetch(apiUrl(`/candidates/${id}`), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateCandidateStatus(id: string, status: string, notes?: string): Promise<CandidateDTO> {
  const res = await fetch(apiUrl(`/candidates/${id}/status`), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify({ status, notes }),
  });
  return handleResponse(res);
}

export async function assignRecruiter(id: string, recruiterId: string, notes?: string): Promise<CandidateDTO> {
  const q = new URLSearchParams({ recruiter_id: recruiterId });
  if (notes) q.set('notes', notes);
  const res = await fetch(apiUrl(`/candidates/${id}/recruiter`) + `?${q.toString()}`, {
    method: 'PUT',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function getCandidateStats(orgId?: string, vacancyId?: string): Promise<RecruitmentStats> {
  const org = orgId || localStorage.getItem('org_id') || 'orion';
  const q = new URLSearchParams();
  if (vacancyId) q.set('vacancy_id', vacancyId);
  const qs = q.toString();
  const res = await fetch(apiUrl(`/candidates/stats/${org}`) + (qs ? `?${qs}` : ''), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function deleteCandidate(id: string): Promise<{ deleted: boolean; id: string }> {
  const res = await fetch(apiUrl(`/candidates/${id}`), {
    method: 'DELETE',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

export async function downloadCandidateCV(applicationId: string, candidateName: string): Promise<void> {
  const res = await fetch(apiUrl(`/candidates/recruiter/candidate/${applicationId}/cv`), {
    headers: await headersWithToken(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Error al descargar CV' }));
    throw new Error(err.detail || 'Error al descargar CV');
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `CV_${candidateName.replace(/\s+/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// =====================
// PROMPTS MANAGEMENT API (Admin)
// =====================

export type PromptLayerDTO = {
  id: string;
  layer_type: string;
  name: string;
  description: string | null;
  content: string;
  version: number;
  is_locked: boolean;
  is_active: boolean;
  org_id: string | null;
  thread_id: string | null;
  whatsapp_number: string | null;
  response_schema: ResponseSchema | null;
  context_config: ContextConfig | null;
  created_at: string | null;
  updated_at: string | null;
};

/** Schema de campos extra que el LLM debe incluir en su respuesta JSON */
export interface ResponseSchemaField {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'array';
  label: string;
  description: string;
  required: boolean;
}

export interface ResponseSchema {
  fields: ResponseSchemaField[];
}

/** Config de secciones de contexto a inyectar en el prompt */
export interface ContextConfig {
  sections: Record<string, boolean>;
  custom_variables: Record<string, string>;
}

/** Distribución numérica por rango */
export interface FieldDistributionRange {
  range: string;
  count: number;
}

/** Top value para campos string */
export interface FieldTopValue {
  value: string;
  count: number;
}

/** Resumen de un campo en analytics */
export interface FieldSummary {
  type: 'string' | 'number' | 'boolean' | 'array';
  count: number;
  avg?: number;
  min?: number;
  max?: number;
  distribution?: FieldDistributionRange[];
  top_values?: FieldTopValue[];
}

/** Estadísticas de campos custom extraídos por el LLM */
export interface CustomFieldStats {
  org_id: string;
  period: { from: string; to: string };
  total_conversations: number;
  fields_summary: Record<string, FieldSummary>;
}

// List all prompt layers
export async function getPromptLayers(): Promise<{ success: boolean; layers: PromptLayerDTO[] }> {
  const res = await fetch(apiUrl('/prompts/'), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Get comercial prompt
export async function getComercialPrompt(waNumber?: string): Promise<{ success: boolean; prompt: PromptLayerDTO | null }> {
  const q = waNumber ? `?whatsapp_number=${encodeURIComponent(waNumber)}` : '';
  const res = await fetch(apiUrl('/prompts/comercial') + q, {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// List all comercial prompts
export async function listComercialPrompts(): Promise<{ success: boolean; prompts: PromptLayerDTO[]; count: number }> {
  const res = await fetch(apiUrl('/prompts/comercial/all'), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Create comercial prompt
export async function createComercialPrompt(content: string, name: string, waNumber?: string): Promise<any> {
  const res = await fetch(apiUrl('/prompts/comercial'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify({ layer_type: 'comercial', content, name, whatsapp_number: waNumber || undefined }),
  });
  return handleResponse(res);
}

// Update comercial prompt
export async function updateComercialPrompt(content: string, name?: string, waNumber?: string): Promise<any> {
  const q = waNumber ? `?whatsapp_number=${encodeURIComponent(waNumber)}` : '';
  const res = await fetch(apiUrl('/prompts/comercial') + q, {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify({ content, name }),
  });
  return handleResponse(res);
}

// Get conciencia prompt
export async function getConcienciaPrompt(): Promise<{ success: boolean; prompt: PromptLayerDTO }> {
  const res = await fetch(apiUrl('/prompts/conciencia'), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Update conciencia prompt (CTO only)
export async function updateConcienciaPrompt(content: string, name?: string): Promise<any> {
  const res = await fetch(apiUrl('/prompts/conciencia'), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify({ content, name }),
  });
  return handleResponse(res);
}

// Get org base prompt
export async function getOrgBasePrompt(): Promise<{ success: boolean; prompt: PromptLayerDTO | null }> {
  const res = await fetch(apiUrl('/prompts/org-base'), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// Update org base prompt
export async function updateOrgBasePrompt(
  content: string,
  name?: string,
  response_schema?: ResponseSchema | null,
  context_config?: ContextConfig | null,
): Promise<any> {
  const body: Record<string, any> = { content };
  if (name !== undefined) body.name = name;
  if (response_schema !== undefined) body.response_schema = response_schema;
  if (context_config !== undefined) body.context_config = context_config;
  const res = await fetch(apiUrl('/prompts/org-base'), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// Get custom field analytics (extra_fields from ConversationLog)
export async function getCustomFieldAnalytics(params?: {
  date_from?: string;
  date_to?: string;
  group_by?: string;
}): Promise<CustomFieldStats> {
  const orgId = localStorage.getItem('org_id') || 'orion';
  const qs = new URLSearchParams();
  if (params?.date_from) qs.set('date_from', params.date_from);
  if (params?.date_to) qs.set('date_to', params.date_to);
  if (params?.group_by) qs.set('group_by', params.group_by);
  const q = qs.toString();
  const res = await fetch(apiUrl(`/orgs/${orgId}/analytics/custom-fields${q ? `?${q}` : ''}`), {
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

// =====================
// THREADS START (create new thread)
// =====================

export async function startThread(params?: { new_thread?: boolean; site_id?: string; visitor_id?: string }): Promise<any> {
  const res = await fetch(apiUrl('/threads/start'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify({
      site_id: params?.site_id || window.location.host,
      visitor_id: params?.visitor_id || 'admin-' + Date.now(),
      new_thread: params?.new_thread ?? true,
    }),
  });
  return handleResponse(res);
}


// ============== Twilio Diagnostics ==============

export interface TwilioMessage {
  sid: string;
  from: string;
  to: string;
  body: string;
  status: string;
  direction: string;
  date_sent: string | null;
  date_created: string;
  error_code: number | null;
  error_message: string | null;
  price: string | null;
  price_unit: string | null;
  num_segments: string | null;
  num_media: string | null;
}

export interface TwilioAccountInfo {
  account_sid: string;
  friendly_name: string;
  status: string;
  type: string;
  date_created: string;
  balance: string;
  currency: string;
  whatsapp_number: string;
}

export interface TwilioUsageDaily {
  date: string;
  category: string;
  count: number;
  price: number;
  price_unit: string;
}

export interface TwilioUsageCategory {
  category: string;
  description: string;
  count: string;
  price: string;
  price_unit: string;
}

export interface TwilioUsageData {
  period: string;
  daily_breakdown: TwilioUsageDaily[];
  total_sms_count: number;
  total_sms_cost_usd: number;
  monthly_categories: TwilioUsageCategory[];
}

export interface TwilioResponse {
  success: boolean;
  data?: any;
  error?: string;
}

// Información de la cuenta
export async function getTwilioAccount(): Promise<TwilioResponse> {
  const res = await fetch(apiUrl('/twilio/account'), { headers: await headersWithToken() });
  return handleResponse(res);
}

// Listar mensajes con filtros
export async function getTwilioMessages(filters?: {
  phone?: string; direction?: string; status?: string; days?: number; limit?: number;
}): Promise<TwilioResponse> {
  const params = new URLSearchParams();
  if (filters?.phone) params.set('phone', filters.phone);
  if (filters?.direction) params.set('direction', filters.direction);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.days) params.set('days', String(filters.days));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(apiUrl(`/twilio/messages${qs}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

// Detalle de un mensaje
export async function getTwilioMessageDetail(sid: string): Promise<TwilioResponse> {
  const res = await fetch(apiUrl(`/twilio/messages/${sid}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

// Errores de entrega
export async function getTwilioErrors(days: number = 7, limit: number = 50): Promise<TwilioResponse> {
  const res = await fetch(apiUrl(`/twilio/errors?days=${days}&limit=${limit}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

// Uso y costos
export async function getTwilioUsage(): Promise<TwilioResponse> {
  const res = await fetch(apiUrl('/twilio/usage'), { headers: await headersWithToken() });
  return handleResponse(res);
}

// Info del número
export async function getTwilioPhoneInfo(): Promise<TwilioResponse> {
  const res = await fetch(apiUrl('/twilio/phone-info'), { headers: await headersWithToken() });
  return handleResponse(res);
}

// ── Módulos (habilitados/deshabilitados por org) ─────────────────────────────
export interface ModuleDef {
  key: string;
  label: string;
  enabled: boolean;
}

export interface ModulesResponse {
  modules: ModuleDef[];
  is_admin: boolean;
  is_dev: boolean;
  user_role: string | null;
  user_roles: string[] | null;
  /** Archetype de la org (`healthcare-clinic`, `agency-travel`, …).
   *  Frontend lo usa para overrides de label/categoría en el sidebar
   *  + para decidir default route + home view per-archetype. */
  archetype?: string | null;
  /** Secciones que la org quiere ver en el menú (claves del registry). null =
   *  sin restricción. Solo navegación: no apaga módulos ni herramientas. */
  menu_visible?: string[] | null;
  /** Org soberana (solo su modelo gobernado; sin servicios comerciales). Aditivo:
   *  backends viejos no lo mandan y el front cae al arquetipo. */
  sovereign?: boolean;
}

/** Obtiene la lista de módulos y su estado on/off */
export async function getModules(): Promise<ModulesResponse> {
  const res = await fetch(apiUrl('/org/modules'), { headers: await headersWithToken() });
  return handleResponse(res);
}

/** Actualiza módulos (solo admin). Envía un dict parcial {key: bool} */
export async function updateModules(modules: Record<string, boolean>): Promise<ModulesResponse> {
  const res = await fetch(apiUrl('/org/modules'), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify({ modules }),
  });
  return handleResponse(res);
}

// ── Configuración → Módulos (admin/cto de la org; org del JWT) ──────────────
// Backend: Orion api/routes/org_module_config.py. `available` lo calcula el
// servidor (hoy siempre true): cuando se restrinjan módulos por plan, la UI ya
// lo respeta sin cambios.

export type ModuleSource = 'org' | 'base' | 'opt_in' | 'arquetipo' | 'todo';

export interface OrgModuleItem {
  key: string;
  label: string;
  group: string;
  description: string;
  enabled: boolean;
  source: ModuleSource;
  available: boolean;
  unavailable_reason: string | null;
  requirement: string | null;
  /** Tools del agente que otorga este módulo en la org. */
  agent_tools: number;
}

export interface OrgModuleChange {
  at: string;
  key: string;
  enabled_before: boolean | null;
  enabled_after: boolean;
  actor: string | null;
  via: string | null;
  reason: string | null;
}

export interface OrgModuleCatalog {
  org_id: string;
  archetype: string | null;
  can_edit: boolean;
  groups: string[];
  modules: OrgModuleItem[];
  history: OrgModuleChange[];
}

export interface OrgModuleImpactTool {
  tool_id: string;
  name: string;
  summary: string;
  usos_30d: number;
}

export interface OrgModuleImpact {
  key: string;
  enabled_now: boolean;
  enable: boolean;
  agent_tools: OrgModuleImpactTool[];
  agent_tools_count: number;
  agent_uses_30d: number;
}

/** Errores con el `detail` del backend legible (a veces es objeto {message, impact}). */
async function moduleConfigResponse<T>(res: Response): Promise<T> {
  if (res.ok) return res.json();
  if (res.status === 401) return handleResponse(res);
  let msg = `Error ${res.status}`;
  try {
    const body = await res.json();
    const d = body?.detail;
    msg = typeof d === 'string' ? d : d?.message || msg;
  } catch {
    /* sin JSON */
  }
  throw Object.assign(new Error(msg), { status: res.status });
}

export async function getOrgModuleCatalog(): Promise<OrgModuleCatalog> {
  const res = await fetch(apiUrl('/org/modules/catalog'), { headers: await headersWithToken() });
  return moduleConfigResponse(res);
}

export async function getOrgModuleImpact(key: string, enable: boolean): Promise<OrgModuleImpact> {
  const res = await fetch(apiUrl(`/org/modules/${encodeURIComponent(key)}/impact?enabled=${enable}`), {
    headers: await headersWithToken(),
  });
  return moduleConfigResponse(res);
}

export async function setOrgModule(
  key: string,
  enabled: boolean,
  opts: { confirm?: boolean; reason?: string } = {},
): Promise<{ org_id: string; changed: Array<{ key: string; before: boolean | null; after: boolean }>; module: OrgModuleItem | null }> {
  const res = await fetch(apiUrl(`/org/modules/${encodeURIComponent(key)}`), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify({ enabled, confirm: !!opts.confirm, reason: opts.reason?.trim() || null }),
  });
  return moduleConfigResponse(res);
}

// ── Recruitment Dashboard (Admin) ────────────────────────────────────────────

export interface RecruiterStats {
  id: string;
  name: string;
  username: string;
  phone: string | null;
  assigned_vacancies: number;
  assigned_candidates: number;
  in_process: number;
  hired: number;
}

export interface VacancyStats {
  id: string;
  title: string;
  status: string;
  department: string | null;
  applications_count: number;
  avg_score: number | null;
  status_breakdown: Record<string, number>;
  recruiter: {
    id: string | null;
    name: string | null;
    phone: string | null;
  } | null;
}

export interface TopCandidate {
  id: string;
  name: string;
  score: number;
  status: string;
}

export interface RecruitmentDashboardResponse {
  total_applications: number;
  by_status: Record<string, number>;
  avg_ai_score: number | null;
  top_candidates: TopCandidate[];
  vacancies: VacancyStats[];
  recruiters: RecruiterStats[];
}

/** Obtiene estadísticas completas del dashboard de recruitment (admin) */
export async function getRecruitmentDashboard(): Promise<RecruitmentDashboardResponse> {
  const res = await fetch(apiUrl('/candidates/admin/dashboard'), { 
    headers: await headersWithToken() 
  });
  return handleResponse(res);
}

// ── Travel Admin (Django MCP Backend) ────────────────────────────────────────

export interface OrgBackend {
  id: string;
  org_id: string;
  backend_url: string;
  display_name: string | null;
  enabled: boolean;
  last_sync_at: string | null;
}

export interface MCPTool {
  tool_id: string;
  org_id: string;
  name: string;
  description: string | null;
  source_type: 'external' | 'builtin' | 'dynamic';
  status: 'active' | 'deprecated';
  category: string | null;
  external_config: {
    method: string;
    path: string;
    backend_org_id?: string;
  } | null;
  input_schema: Record<string, unknown> | null;
}

/** Lista backends conectados (Django MCP) */
export async function getOrgBackends(): Promise<OrgBackend[]> {
  const res = await fetch(apiUrl('/admin/org-backends'), { headers: await headersWithToken() });
  return handleResponse(res);
}

/** Detalle de un backend */
export async function getOrgBackend(orgId: string): Promise<OrgBackend> {
  const res = await fetch(apiUrl(`/admin/org-backends/${orgId}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

/** Registrar un nuevo backend */
export async function createOrgBackend(data: {
  org_id: string;
  backend_url: string;
  api_key_raw: string;
  display_name?: string;
}): Promise<OrgBackend> {
  const res = await fetch(apiUrl('/admin/org-backends'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

/** Actualizar backend */
export async function updateOrgBackend(orgId: string, data: Partial<{
  backend_url: string;
  api_key_raw: string;
  display_name: string;
  enabled: boolean;
}>): Promise<OrgBackend> {
  const res = await fetch(apiUrl(`/admin/org-backends/${orgId}`), {
    method: 'PATCH',
    headers: await headersWithToken(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

/** Eliminar backend */
export async function deleteOrgBackend(orgId: string): Promise<void> {
  const res = await fetch(apiUrl(`/admin/org-backends/${orgId}`), {
    method: 'DELETE',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

/** Sincronizar tools de un backend */
export async function syncOrgTools(orgId: string): Promise<{ detail: string; tools_synced: number }> {
  const res = await fetch(apiUrl(`/admin/orgs/${orgId}/sync-tools`), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

/** Sincronizar tools de todos los backends */
export async function syncAllOrgTools(): Promise<{ detail: string }> {
  const res = await fetch(apiUrl('/admin/orgs/sync-all-tools'), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

/** Listar MCP tools (externas) con filtros opcionales */
export async function getMCPTools(params?: {
  source_type?: string;
  status?: string;
  category?: string;
  search?: string;
}): Promise<MCPTool[]> {
  const qs = new URLSearchParams();
  if (params?.source_type) qs.set('source_type', params.source_type);
  if (params?.status) qs.set('status', params.status);
  if (params?.category) qs.set('category', params.category);
  if (params?.search) qs.set('search', params.search);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const res = await fetch(apiUrl(`/admin/mcp-tools${query}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

// ── Automatizaciones ─────────────────────────────────────────────────────────

export type AutomationTrigger = 'cron' | 'event' | 'webhook' | 'manual';
export type AutomationStatus = 'active' | 'paused' | 'error' | 'draft';
export type AutomationRunStatus = 'running' | 'success' | 'partial' | 'failed';

export interface Automation {
  id: string;
  org_id: string;
  name: string;
  description: string;
  trigger_type: AutomationTrigger;
  cron_expression: string | null;
  event_type: string | null;
  prompt_template: string;
  tools_allowed: string[];
  model: string;
  max_iterations: number;
  status: AutomationStatus;
  last_run_at: string | null;
  last_run_status: AutomationRunStatus | null;
  last_run_summary: string | null;
  run_count: number;
  error_count: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface AutomationRun {
  id: string;
  automation_id: string;
  started_at: string;
  finished_at: string | null;
  status: AutomationRunStatus;
  prompt_sent: string;
  tool_calls: Record<string, any>[];
  response_summary: string;
  tokens_used: number;
  error_message: string | null;
}

export interface AutomationListResponse {
  automations: Automation[];
  count: number;
}

export interface AutomationRunListResponse {
  runs: AutomationRun[];
  count: number;
}

/** Lista automatizaciones de la org */
export async function listAutomations(params?: {
  status?: string;
}): Promise<AutomationListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const res = await fetch(apiUrl(`/automations${query}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

/** Obtener una automatización por ID */
export async function getAutomation(id: string): Promise<Automation> {
  const res = await fetch(apiUrl(`/automations/${id}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

/** Crear automatización */
export async function createAutomation(body: {
  name: string;
  description?: string;
  trigger_type: AutomationTrigger;
  cron_expression?: string;
  event_type?: string;
  prompt_template: string;
  tools_allowed?: string[];
  model?: string;
  max_iterations?: number;
}): Promise<Automation> {
  const res = await fetch(apiUrl('/automations'), {
    method: 'POST',
    headers: await headersWithToken(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

/** Actualizar automatización */
export async function updateAutomation(id: string, body: Partial<{
  name: string;
  description: string;
  trigger_type: AutomationTrigger;
  cron_expression: string | null;
  event_type: string | null;
  prompt_template: string;
  tools_allowed: string[];
  model: string;
  max_iterations: number;
  status: AutomationStatus;
}>): Promise<Automation> {
  const res = await fetch(apiUrl(`/automations/${id}`), {
    method: 'PUT',
    headers: await headersWithToken(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

/** Eliminar automatización */
export async function deleteAutomation(id: string): Promise<{ detail: string }> {
  const res = await fetch(apiUrl(`/automations/${id}`), {
    method: 'DELETE',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

/** Ejecutar manualmente una automatización */
export async function runAutomation(id: string): Promise<AutomationRun> {
  const res = await fetch(apiUrl(`/automations/${id}/run`), {
    method: 'POST',
    headers: await headersWithToken(),
  });
  return handleResponse(res);
}

/** Historial de ejecuciones */
export async function listAutomationRuns(automationId: string, params?: {
  limit?: number;
  offset?: number;
}): Promise<AutomationRunListResponse> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const res = await fetch(apiUrl(`/automations/${automationId}/history${query}`), { headers: await headersWithToken() });
  return handleResponse(res);
}

// ── Templates de sitio público ──────────────────────────────────────────────
// Estos endpoints viven en Django (/api/v1/...), NO en Orion FastAPI.
// Se enrutan por el mismo proxy que api_travel.ts: /django-api/ → backend.efficientai.es
// Absolute path (leading slash) — matches vite.config.ts proxy key.
// A relative value se resuelve contra la URL actual y bajo react-router
// v7 la URL puede ser profunda (`/m/mail/mailboxes`) → el POST termina
// en `/m/mail/django-api/...` y devuelve 405. Mismo fix que
// api_marketing.ts / api_whatsapp.ts / etc.
const DJANGO_PROXY_BASE = '/django-api';

export async function djangoApiCall<T>(path: string, init?: RequestInit): Promise<T> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  // Guard: if no token yet, throw a typed error so callers can retry when
  // auth:token-ready fires — avoids making an unauthenticated request that
  // would trigger the 401 → auth:logout storm in handleResponse.
  if (!token) {
    const err = new Error('Sin sesión iniciada') as Error & { code: string };
    err.code = 'NO_TOKEN';
    throw err;
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const url = `${DJANGO_PROXY_BASE}/api/v1${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
  return handleResponse(res);
}

export interface SiteTemplateTheme {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    [key: string]: string;
  };
  fonts: { heading: string; body: string };
  borderRadius: string;
  shadows: string;
}

export interface SiteTemplateComponents {
  hero: string;
  navbar: string;
  tripCard: string;
  blogCard: string;
  footer: string;
  [key: string]: string;
}

export interface SiteTemplate {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  preview_image: string | null;
  theme: SiteTemplateTheme;
  components: SiteTemplateComponents;
  sections_order: string[];
  animations: Record<string, any>;
  is_default: boolean;
  created_at: string;
}

export interface OrgTemplateConfig {
  template: SiteTemplate;
  custom_overrides: {
    colors?: Partial<SiteTemplateTheme['colors']>;
    fonts?: Partial<SiteTemplateTheme['fonts']>;
    [key: string]: any;
  } | null;
  assigned_at: string;
}

export interface TemplateListResponse {
  templates: SiteTemplate[];
  count: number;
}

/** Obtener template activo de la org */
export async function getActiveTemplate(): Promise<OrgTemplateConfig> {
  return djangoApiCall('/admin/templates/assignment/current');
}

/** Listar templates disponibles para la categoría de la org */
export async function listTemplates(): Promise<TemplateListResponse> {
  return djangoApiCall('/admin/templates/');
}

/** Asignar template a la org */
export async function assignTemplate(templateId: string): Promise<{ detail: string }> {
  return djangoApiCall('/admin/templates/assign', {
    method: 'POST',
    body: JSON.stringify({ template_id: templateId }),
  });
}

/** Guardar overrides de color/fonts encima del template base */
export async function saveTemplateOverrides(overrides: {
  colors?: Partial<SiteTemplateTheme['colors']>;
  fonts?: Partial<SiteTemplateTheme['fonts']>;
}): Promise<{ detail: string }> {
  return djangoApiCall('/admin/templates/overrides', {
    method: 'PUT',
    body: JSON.stringify(overrides),
  });
}

// ── Site Config (secciones por org) ─────────────────────────────────────────

export interface SiteSection {
  key: string;
  is_enabled: boolean;
  order: number;
  config: Record<string, any>;
}

export interface SiteConfig {
  sections: SiteSection[];
}

/** Config pública del sitio — no requiere auth */
export async function getSiteConfig(orgId?: string): Promise<SiteConfig> {
  const qs = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
  const res = await fetch(`${DJANGO_PROXY_BASE}/api/v1/site-config/${qs}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  return handleResponse(res);
}

/** Config del admin — requiere JWT */
export async function getAdminSiteConfig(): Promise<SiteConfig> {
  return djangoApiCall('/admin/site-config/');
}

/** Actualizar secciones — PUT /api/v1/admin/site-config/ */
export async function updateAdminSiteConfig(sections: SiteSection[]): Promise<{ detail: string }> {
  return djangoApiCall('/admin/site-config/', {
    method: 'PUT',
    body: JSON.stringify({ sections }),
  });
}

// ── Correo multi-tenant (MailDomain + MailAccount) ───────────────────────────
// Endpoints: /api/v1/admin/mail/... → Django proxy

export interface MailDomain {
  id: string;
  org: string;
  domain: string;
  mail_hostname: string;
  is_active: boolean;
  dkim_public_key: string | null;
  dkim_selector: string;
  spf_record: string | null;
  dmarc_record: string | null;
  dns_verified: boolean;
  dns_last_checked: string | null;
  created_at: string;
  updated_at: string;
}

export interface MailAccount {
  id: string;
  org: string;
  domain: string;
  email: string;
  display_name: string;
  is_active: boolean;
  quota_mb: number;
  is_smtp_sender: boolean;
  created_at: string;
  updated_at: string;
}

export interface MailDnsRecords {
  a: string;
  mx: string;
  spf: string;
  dkim_name: string;
  dkim_value: string;
  dmarc: string;
}

export interface MailDomainListResponse {
  domains: MailDomain[];
  count: number;
}

export interface MailAccountListResponse {
  accounts: MailAccount[];
  count: number;
}

/** Listar dominios de correo de la org */
export async function listMailDomains(): Promise<MailDomainListResponse> {
  return djangoApiCall('/admin/mail/domains/');
}

/** Agregar un nuevo dominio */
export async function createMailDomain(domain: string): Promise<MailDomain> {
  return djangoApiCall('/admin/mail/domains/', {
    method: 'POST',
    body: JSON.stringify({ domain }),
  });
}

/** Ver registros DNS requeridos para un dominio */
export async function getMailDomainDns(id: string): Promise<MailDnsRecords> {
  return djangoApiCall(`/admin/mail/domains/${id}/dns/`);
}

/** Verificar DNS y marcar dominio como verificado */
export async function verifyMailDomain(id: string): Promise<{ detail: string; dns_verified: boolean }> {
  return djangoApiCall(`/admin/mail/domains/${id}/verify/`, { method: 'POST' });
}

/** Listar cuentas de correo de la org */
export async function listMailAccounts(): Promise<MailAccountListResponse> {
  return djangoApiCall('/admin/mail/accounts/');
}

/** Crear una cuenta de correo */
export async function createMailAccount(data: {
  domain_id: string;
  local_part: string;
  display_name: string;
  password: string;
  quota_mb?: number;
  is_smtp_sender?: boolean;
}): Promise<MailAccount> {
  return djangoApiCall('/admin/mail/accounts/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Actualizar cuota, display_name o estado de una cuenta */
export async function updateMailAccount(
  id: string,
  data: { display_name?: string; quota_mb?: number; is_active?: boolean },
): Promise<MailAccount> {
  return djangoApiCall(`/admin/mail/accounts/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/** Resetear contraseña de una cuenta */
export async function resetMailAccountPassword(
  id: string,
  new_password: string,
): Promise<{ detail: string }> {
  return djangoApiCall(`/admin/mail/accounts/${id}/reset-password/`, {
    method: 'POST',
    body: JSON.stringify({ new_password }),
  });
}

/** Eliminar cuenta de correo */
export async function deleteMailAccount(id: string): Promise<{ detail: string }> {
  return djangoApiCall(`/admin/mail/accounts/${id}/`, { method: 'DELETE' });
}

// ── Mailboxes por org (ticket 064) ───────────────────────────────────────────
// Endpoints: /api/v1/admin/mail/mailboxes/ — scoped al token del org_admin.
// El password en texto plano sólo se devuelve UNA vez en la respuesta de creación.

export interface Mailbox {
  email: string;
  local_part: string;
  domain: string;
  status: 'active' | 'suspended';
  created_at: string;
}

export interface MailboxCreated extends Mailbox {
  /** Contraseña en texto plano — sólo presente en la respuesta de creación */
  password: string;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
}

export interface MailboxListResponse {
  total: number;
  mailboxes: Mailbox[];
}

/** Listar mailboxes de la org */
export async function listMailboxes(): Promise<MailboxListResponse> {
  return djangoApiCall('/admin/mail/mailboxes/');
}

/** Crear mailbox — el password se devuelve en texto plano UNA sola vez */
export async function createMailbox(data: {
  local_part: string;
  password: string;
}): Promise<MailboxCreated> {
  return djangoApiCall('/admin/mail/mailboxes/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Eliminar mailbox por dirección email completa — retorna 204 */
export async function deleteMailbox(email: string): Promise<void> {
  await djangoApiCall(`/admin/mail/mailboxes/${encodeURIComponent(email)}/`, {
    method: 'DELETE',
  });
}

// ── SMTP config por org (ticket 067) ────────────────────────────────────────
// Endpoints: /api/v1/admin/company/smtp/ — configuración SMTP de notificaciones.
// El backend Django ya tiene los campos en Company; solo falta la UI.

export interface SmtpConfig {
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  /** Solo viene como '****' en GET; se envía real en PATCH */
  smtp_password: string;
  smtp_from_email: string;
  smtp_use_ssl: boolean;
  smtp_use_tls: boolean;
  email_provider: 'smtp' | 'google_oauth';
  /** true si smtp_host ya está configurado */
  is_configured: boolean;
}

export interface SmtpTestResult {
  success: boolean;
  detail: string;
}

/** Leer config SMTP actual (password viene enmascarado) */
export async function getSmtpConfig(): Promise<SmtpConfig> {
  return djangoApiCall('/admin/company/smtp/');
}

/** Actualizar config SMTP */
export async function updateSmtpConfig(data: Partial<Omit<SmtpConfig, 'is_configured'>>): Promise<SmtpConfig> {
  return djangoApiCall('/admin/company/smtp/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/** Enviar email de prueba con la config actual */
export async function sendSmtpTestEmail(): Promise<SmtpTestResult> {
  return djangoApiCall('/admin/company/smtp/test/', {
    method: 'POST',
  });
}


// ──────────────────────────────────────────────────────────────
// Guía de uso personalizada de la org (Company.usage_guide)
// Endpoints: /api/v1/admin/company/usage-guide
// ──────────────────────────────────────────────────────────────

export interface UsageGuide {
  usage_guide: string;
}

/** Lee la guía de uso personalizada de la org (markdown). */
export async function getUsageGuide(): Promise<UsageGuide> {
  return djangoApiCall('/admin/company/usage-guide');
}

/** Actualiza la guía de uso de la org. */
export async function updateUsageGuide(usage_guide: string): Promise<UsageGuide> {
  return djangoApiCall('/admin/company/usage-guide', {
    method: 'PUT',
    body: JSON.stringify({ usage_guide }),
  });
}

// ──────────────────────────────────────────────────────────────
// Workflow playbooks per-org (OrgWorkflow)
// Endpoints: /api/v1/admin/company/workflows
// ──────────────────────────────────────────────────────────────

export interface OrgWorkflowSummary {
  slug: string;
  title: string;
  description: string;
  when_to_use: string;
  tools_used: string[];
  tags: string[];
  agent_readable: boolean;
}

export interface OrgWorkflow extends OrgWorkflowSummary {
  steps: string;
}

export interface OrgWorkflowInput {
  slug: string;
  title: string;
  description?: string;
  when_to_use?: string;
  steps?: string;
  tools_used?: string[];
  tags?: string[];
  agent_readable?: boolean;
}

export async function listWorkflows(): Promise<OrgWorkflowSummary[]> {
  return djangoApiCall('/admin/company/workflows');
}

export async function getWorkflowFull(slug: string): Promise<OrgWorkflow> {
  return djangoApiCall(`/admin/company/workflows/${encodeURIComponent(slug)}`);
}

export async function createWorkflow(data: OrgWorkflowInput): Promise<OrgWorkflow> {
  return djangoApiCall('/admin/company/workflows', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWorkflow(slug: string, data: Partial<OrgWorkflowInput>): Promise<OrgWorkflow> {
  return djangoApiCall(`/admin/company/workflows/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWorkflow(slug: string): Promise<void> {
  await djangoApiCall(`/admin/company/workflows/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
  });
}

// ── LLM Providers per-org (TKT-97) ──────────────────────────
// Backend en mcp_django apps/llm_providers/ — cada org guarda sus
// propias API keys de Anthropic/OpenAI/Google/etc.

export type LLMProviderChoice = {
  value: string;
  label: string;
  /** ¿El sistema tiene env var configurada para este proveedor? Si false,
   *  el cliente DEBE setear su propia key (no hay fallback global). */
  has_global_fallback?: boolean;
  /** Modelo que el sistema usa por default — placeholder del input. */
  default_model_hint?: string;
};

export interface OrgLLMProvider {
  id: string;
  org_id: string;
  provider: string;
  provider_display: string;
  default_model: string;
  is_active: boolean;
  notes: string;
  has_api_key: boolean;
  last_tested_at: string | null;
  last_test_status: string;
  last_test_error: string;
  created_at: string;
  updated_at: string;
}

export interface OrgLLMProviderInput {
  provider: string;
  api_key: string;
  default_model?: string;
  is_active?: boolean;
  notes?: string;
}

export interface OrgLLMProviderPatchInput {
  api_key?: string;
  default_model?: string;
  is_active?: boolean;
  notes?: string;
}

export interface TestConnectionResult {
  ok: boolean;
  provider: string;
  model_used: string;
  latency_ms: number;
  error: string;
  tested_at: string;
}

export async function listLLMProviderChoices(): Promise<LLMProviderChoice[]> {
  return djangoApiCall('/admin/llm-providers/choices');
}

export async function listLLMProviders(): Promise<OrgLLMProvider[]> {
  return djangoApiCall('/admin/llm-providers/');
}

export async function createLLMProvider(data: OrgLLMProviderInput): Promise<OrgLLMProvider> {
  return djangoApiCall('/admin/llm-providers/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateLLMProvider(id: string, data: OrgLLMProviderPatchInput): Promise<OrgLLMProvider> {
  return djangoApiCall(`/admin/llm-providers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteLLMProvider(id: string): Promise<void> {
  await djangoApiCall(`/admin/llm-providers/${id}`, { method: 'DELETE' });
}

export async function testLLMProvider(id: string): Promise<TestConnectionResult> {
  return djangoApiCall(`/admin/llm-providers/${id}/test`, { method: 'POST' });
}
